import { AGILITY_APP_TITLE } from '../../../secure/funding/constants/agility-title.const';
import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { TranslateService } from '@ngx-translate/core';
import {
  GetTenantNameByTenantIdGQL,
  GetTenantNameByTenantIdQuery,
  GetTenantLogoAndNameGQL,
} from 'src/app/graphql/generated';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { CustomEventTokens } from 'src/app/shared/models/custom-event-tokens';
import { FileHandlingService } from '../../services/files/file-handling.service';
import { GetSubTenantLegalNameQuery } from '../../services/graphql-apis/subTenant/subtenant-graphql.types';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { SubtenantGraphqlApisService } from '../../services/graphql-apis/subTenant/subtenant-graphql-apis.service';
import { UserService } from '../../services/user.service';
import { FeatureFlagKeys } from '../../models/feature-flags.model';
import { UserType } from '../../models/user-enumeration';
import { AUTHENTICATION_STATUS } from '../../services/authentication/authentication-utilities.service';
import { PermissionService } from '../../guards/permission/service/permission.service';
import { PERMISSIONS } from '../../constants/permissions.constants';
import { EXPERIMENTAL_FEATURES_ENVIRONMENTS } from '@experimental-features/guards/experimental-features.guard';
import { environment } from 'src/environments/environment';
import { LOGO_PNG_MIME_TYPE } from 'src/app/shared/constants/input-file-types.constants';
import {
  AnalyticsEvents,
  type GaEventParams,
} from 'src/app/analytics/analytics-events';
import { TenantConfigurationsService } from './services/tenant-configurations/tenant-configurations.service';
// THEMING CODE: These are to be used for testing themes and dark mode, once we have time to implement it properly
// these will be able to be removed.
// import { updatePreset } from '@primeng/themes';
// import { AgilityPreset, WABPreset } from 'src/assets/css/themes/presets';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  userType!: string;
  isMobileNavTurnedOn = false;
  isLargeScreen = false;
  private _getScreenWidth = 0;
  items: MenuItem[] = [];
  userName?: string;
  name!: string | undefined;
  recentlyViewedLoanSize: number = 4;
  tenantUser: string = 'TENANT';
  agilityAppTitle = AGILITY_APP_TITLE;
  readonly analyticsEvents = AnalyticsEvents;

  readonly headerLogoButtonClickParams: GaEventParams = {
    element_id: 'header-logo-button',
    feature: 'header',
    label: 'Agility Logo',
  };
  isIntelligenceEnabled = false;
  tenantLogoFile: File | null = null;
  logoUrl: string = '../../../../assets/images/agility-logo.svg';

  readonly defaultLogoMimeType: string = LOGO_PNG_MIME_TYPE;

  // Observable for template to check authentication status
  isAuthenticated$!: Observable<boolean>;
  hasAgilityIntelligencePermission = false;
  agilityIntelligenceTooltip = '';

  // THEMING CODE:Temp for theme demo
  // currentPreset = AgilityPreset;

  constructor(
    public readonly translate: TranslateService,
    private readonly authenticationService: AuthenticationService,
    public readonly router: Router,
    public readonly localStorage: LocalStorageService,
    private readonly userService: UserService,
    private readonly subtenantGraphqlApisService: SubtenantGraphqlApisService,
    private readonly getTenantNameByTenantIdGQL: GetTenantNameByTenantIdGQL,
    private readonly getTenantLogoAndNameGQL: GetTenantLogoAndNameGQL,
    private readonly fileHandlingService: FileHandlingService,
    public readonly permissionService: PermissionService,
    private readonly tenantConfigurationsService: TenantConfigurationsService
  ) {
    this.userType = this.userService.userType;
  }

  ngOnInit(): void {
    this.isAuthenticated$ = this.authenticationService.authState$.pipe(
      map(
        (authState) => authState.status === AUTHENTICATION_STATUS.AUTHENTICATED
      )
    );
    this.userName = this.localStorage.getLocalStorageItem(
      CustomEventTokens.AGILITY_USER
    )?.claims?.fullName;

    if (!this.userService.isSuperTenant) {
      const tenantId = this.userService.claims?.tenantId ?? '';

      this.getTenantNameByTenantIdGQL
        .fetch({ variables: { tenantId: null } })
        .pipe(
          switchMap((tenantResult) => {
            if (this.userService.userType === UserType.SUB_TENANT) {
              return this.subtenantGraphqlApisService
                .getSubTenantLegalName(this.userService.originatorId)
                .pipe(
                  map((subTenantResult) => ({
                    data: {
                      ...tenantResult.data,
                      ...subTenantResult.data,
                    },
                  }))
                );
            }

            return of({ data: tenantResult.data });
          })
        )
        .subscribe(({ data }) => {
          const { originator } = data as GetSubTenantLegalNameQuery;
          const { tenant } = data as GetTenantNameByTenantIdQuery;

          if (originator || tenant) {
            this.name = originator?.legalName || tenant?.name || this.userName;
            this.isIntelligenceEnabled =
              tenant?.configurations?.intelligence || false;
            this.userService.setFeatureFlag(
              FeatureFlagKeys.financing,
              this.userService.isSubTenant
                ? false
                : !!tenant?.configurations?.financingSource
            );
            if (tenant?.configurations) {
              this.tenantConfigurationsService.setTenantConfigurations(
                tenant?.configurations
              );
            }
            this.getHeaderList();
          }
        });
    } else {
      this.userService.setFeatureFlag(FeatureFlagKeys.financing, false);
      this.getHeaderList();
    }

    this.getTenantLogoAndNameGQL.fetch().subscribe(({ data }) => {
      const logo = data?.tenant?.logo;
      if (logo) {
        let file = this.fileHandlingService.createFileFromContent(
          logo.fileContent,
          logo.documentName!,
          logo.documentType ?? this.defaultLogoMimeType
        );
        this.tenantLogoFile = file;
        this.logoUrl = URL.createObjectURL(file);
      }
      this.userService.setTenantName(data?.tenant?.name || '');
    });

    this.setScreenWidth(window.innerWidth);
    this.isLargeScreen = this.isScreenSizeLarge();

    // Initialize permission and tooltip as fallback
    this.hasAgilityIntelligencePermission =
      this.permissionService.hasPermission({
        permission: PERMISSIONS.agilityIntelligence,
      });
    this.agilityIntelligenceTooltip = this.hasAgilityIntelligencePermission
      ? this.translate.instant('header.nav.tooltips.agilityIntelligence')
      : this.permissionService.tooltip;
  }

  getHeaderList() {
    this.items = [
      {
        label: `Hello, ${this.userName}`,
        title: this.userName,
        styleClass: 'user-name',
        disabled: true,
      },
      {
        label: this.name,
        title: this.name,
        styleClass: 'profile-tenant-name',
        disabled: true,
      },
      {
        label: 'Experimental Features',
        command: () => {
          this.router.navigate(['/secure/experimental-features']);
        },
        visible: EXPERIMENTAL_FEATURES_ENVIRONMENTS.includes(
          environment.profile
        ),
      },
      {
        label: 'View Profile',
        command: () => {
          this.router.navigate(['/secure/profile/view-profile']);
        },
      },
      {
        label: 'Sign Out',
        command: () => {
          this.logOut();
        },
      },
    ];
  }

  onEscKeyDown(): void {
    this.turnOffMobileNav();
  }

  toggleMobileNav(): void {
    this.isMobileNavTurnedOn = !this.isMobileNavTurnedOn;
  }

  // THEMING CODE: These are to be used for testing themes and dark mode, once we have time to implement it properly
  // toggleDark(): void {
  //   const element = document.querySelector('html');
  //   element?.classList.toggle('agility-dark');
  // }

  // toggleTheme(): void {
  //   this.currentPreset =
  //     this.currentPreset === AgilityPreset ? WABPreset : AgilityPreset;
  //   updatePreset(this.currentPreset);
  // }

  turnOffMobileNav(): void {
    this.isMobileNavTurnedOn = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setScreenWidth(window.innerWidth);
    this.isLargeScreen = this.isScreenSizeLarge();
    if (this.isLargeScreen) {
      this.isMobileNavTurnedOn = false;
    }
  }

  isScreenSizeLarge(): boolean {
    if (this.getScreenWidth() >= 1144) {
      return true;
    } else {
      return false;
    }
  }

  getScreenWidth(): number {
    return this._getScreenWidth;
  }

  setScreenWidth(width: number): void {
    this._getScreenWidth = width;
  }

  closePopover(op: Popover): void {
    op.hide();
  }

  logOut(): void {
    this.authenticationService.logout();
  }

  onAgilityIntelligenceClick(): void {
    if (!this.hasAgilityIntelligencePermission) {
      return;
    }
    this.router.navigate(['/secure/dashboard/powerbi']);
  }
}
